import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
	reset,
	initModify,
	change,
	changeParamRestore,
	changeNominees,
	changeAddress,
	changeShippingAddress,
	changeTerms,
	getSimpleReapplicationsConditionsDetailData,
	getAddressData,
	postReapplication,
	changeBusinessAddress,
	getReapplyByInfo,
	setReapplyLoading,
	setReapplyCallbackEnter,
	reset_reapply,
	setDeliveryCouponId as setDeliveryCouponIdAction,
} from 'app/store/modules/homeservice/reapply';
import { trackHomeService } from '@encarpkg/shared';
import { getAddress, getCar, postEncarLoanApply, postLoanConsult } from 'app/api';
import useAuth from 'app/hook/common/useAuth';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';
import useEnlog from 'app/hook/common/useEnlog';
import useEncarUrlBuilder from 'app/hook/common/useEncarUrlBuilder';
import { testFullUrl } from 'app/libs/regEx';
import qs from 'query-string';

const buyHost = process.env.REACT_APP_ENCAR_FEM_BUY;

export default function useHsReapply() {
	const ua = useSsrUserAgent();
	const {
		isInitModify,
		isReapplyLoading,
		param,
		ui,
		validation,
		validationNominees,
		reapplyInfo,
		encarPayHash,
		isReapplyCallbackEnter,
		deliveryCouponId,
	} = useSelector((state) => state.homeservice.reapply, shallowEqual);
	const { userInfo } = useAuth();
	const dispatch = useDispatch();
	const history = useHistory();
	const { backpath, applicationUuid: applicationUuidQs, salesAgentReservationId } = qs.parse(history.location.search);
	const encarUrlBuilder = useEncarUrlBuilder();
	const { enloggingJsonData } = useEnlog();

	const initModifyInfo = useCallback((flag) => dispatch(initModify(flag)), [dispatch]);

	const loadingInfo = useCallback((flag) => dispatch(setReapplyLoading(flag)), [dispatch]);

	const callbackEnterInfo = useCallback((flag) => dispatch(setReapplyCallbackEnter(flag)), [dispatch]);

	const changeInfo = useCallback((payload) => dispatch(change(payload)), [dispatch]);

	const changeParamRestoreInfo = useCallback((payload) => dispatch(changeParamRestore(payload)), [dispatch]);

	const changeNomineesInfo = useCallback((payload) => dispatch(changeNominees(payload)), [dispatch]);

	const changeAddressInfo = useCallback((payload) => dispatch(changeAddress(payload)), [dispatch]);

	const changeShippingAddressInfo = useCallback((payload) => dispatch(changeShippingAddress(payload)), [dispatch]);

	const changeTermsInfo = useCallback((payload) => dispatch(changeTerms(payload)), [dispatch]);

	const getAddressInfo = useCallback((buildingCd, auth) => dispatch(getAddressData(buildingCd, auth)), [dispatch]);

	const getSimpleReapplicationsConditionsDetailDataInfo = useCallback(
		(applicationUuid, auth) => dispatch(getSimpleReapplicationsConditionsDetailData(applicationUuid, auth)),
		[dispatch],
	);

	const reapplyHomeservice = useCallback((carId, data) => dispatch(postReapplication(carId, data)), [dispatch]);

	const resetReapplyInfo = useCallback(() => dispatch(reset_reapply()), [dispatch]);

	const resetInfo = useCallback(() => dispatch(reset()), [dispatch]);

	const setDeliveryCouponId = useCallback((couponId) => dispatch(setDeliveryCouponIdAction(couponId)), [dispatch]);

	const reapplyLoan = useCallback(
		async (carId, data) => {
			try {
				const {
					id,
					estimate: { carPrice },
					applicant,
					extendedWarranty: { type: warrantyType },
					homeServiceCar,
					estimate,
				} = await reapplyHomeservice(carId, data).then((res) => res.data);

				const { paymentMethod } = param;
				const addressData = await getAddress(applicant.nominees[0].shippingAddress.buildingCd);
				const { abbreviationNm } = addressData.data;
				const hsCar = homeServiceCar?.car;
				let nomineesGender;
				if (!applicant.nominees[0].gender) {
					nomineesGender = null;
				} else {
					nomineesGender = applicant.nominees[0].gender === 'MALE' ? 'M' : 'F';
				}
				const loanParam = {
					nm: applicant.name,
					phnno: applicant.phone,
					birth: applicant.nominees[0].birth,
					gender: nomineesGender,
					city: abbreviationNm,
					src: ua().isPc ? 'W' : 'M',
					carnm: hsCar.manufacturerNm + hsCar.modelNm + hsCar.badgeNm,
					rgsid: carId,
					disp: '',
					mdlyr: '',
					plate: hsCar.plate,
					amount: 0,
					hsseq: id,
				};

				if (paymentMethod === 'ECAR_LOAN') {
					// 차량id로 보증타입조회
					if (warrantyType === 'B') {
						loanParam.amount = Math.floor((carPrice - estimate.extendedWarrantyCost) / 10000);
					} else {
						loanParam.amount = Math.floor(carPrice / 10000);
					}
				}

				const cars = await getCar(carId).then((res) => res.data);
				const {
					displacement,
					carAddition: { formYear },
				} = cars[0];

				loanParam.disp = displacement;
				loanParam.mdlyr = formYear;

				if (paymentMethod === 'ENCAR_LOAN') {
					const { homeServiceResponse } = await postEncarLoanApply(loanParam).then((res) => res.data);

					postLoanConsult(id, {
						amount:
							warrantyType === 'B'
								? Math.floor((carPrice - estimate.extendedWarrantyCost) / 10000) * 10000
								: Math.floor(carPrice / 10000) * 10000,
						loanLinkedKey: homeServiceResponse.hsLoanSeq,
					});
				}

				return { id };
			} catch (error) {
				throw error;
			}
		},
		[reapplyHomeservice, param, ua],
	);

	const changeBusinessAddressInfo = useCallback((payload) => dispatch(changeBusinessAddress(payload)), [dispatch]);

	const handleReApplyHomeservice = useCallback(
		async ({ carId, reapplyInfoUuid, gaAbType, termsConsents }) => {
			const { paymentMethod, extendedWarrantyMonth, loanVersion } = param;
			const { hasWarranty } = ui;

			const data = {
				...param,
				latestCancelledApplicationUuid: reapplyInfoUuid,
				nominees: param.nominees[1].name === '' ? param.nominees.filter((v) => v.name !== '') : param.nominees,
				applicationMethod: ua().isPc ? 'WEB' : 'MOBILE',
				...(salesAgentReservationId && { salesAgentReservationId }),
				// TODO: API에서 refundRule 필드 제거 반영 후 삭제 (PL-9029)
				termsAgreement: {
					refundRule: true,
				},
				// 재신청 약관 바텀시트에 노출된 약관 동의 목록 (PL-16647)
				termsConsents,
			};
			const customerLeadApplicationUuid = await reapplyHomeservice(carId, data).then((res) => res.data.applicationUuid);

			const dataTalkCustom = {
				eventType: 'ACTION',
				eventName: '홈서비스신청완료',
				param: {
					leadType: '홈서비스신청완료',
					attributes: 'retry',
					...(hasWarranty && { ewperiod: extendedWarrantyMonth }),
				},
			};
			enloggingJsonData({
				dataTalkCustom,
			});

			// GA4 재신청 완료
			trackHomeService.applyComplete({ abType: gaAbType, isReapply: true, hasCoupon: !!deliveryCouponId });

			if (customerLeadApplicationUuid) {
				resetInfo();
			}

			// fem > buy 도메인이동으로 ga이벤트 유실안되게 보존 - 임시조치
			await new Promise((resolve) => setTimeout(resolve, 500));

			window.location.href = `${buyHost}/home-service/request/complete?carId=${carId}&customerLeadApplicationUuid=${customerLeadApplicationUuid}&payment=${paymentMethod}${loanVersion ? `&loanVersion=${loanVersion}` : ''}${hasWarranty ? `&ewperiod=${extendedWarrantyMonth}` : ''}&isHsReapply=Y`;
		},
		[enloggingJsonData, param, reapplyHomeservice, resetInfo, ua, ui, salesAgentReservationId, deliveryCouponId],
	);

	const setHsReapplyInfo = useCallback(() => {
		if (!userInfo) return;

		if (userInfo) {
			dispatch(getReapplyByInfo(userInfo?.id));
		}
	}, [dispatch, userInfo]);

	const goBackWithQueryString = useCallback(() => {
		callbackEnterInfo(false);
		const fullLegacyUrl = testFullUrl(backpath) ? backpath : encarUrlBuilder().pc(backpath).mobile(backpath);
		const backUrl = decodeURIComponent(fullLegacyUrl);
		const url = `${backUrl}&applicationUuid=${applicationUuidQs}&isOpenRecommendCar=N`;
		window.location.replace(url);
	}, [applicationUuidQs, backpath, callbackEnterInfo, encarUrlBuilder]);

	return {
		resetInfo,
		isInitModify,
		isReapplyLoading,
		param,
		ui,
		validation,
		validationNominees,
		initModifyInfo,
		loadingInfo,
		reapplyInfo,
		encarPayHash,
		deliveryCouponId,
		setDeliveryCouponId,
		changeInfo,
		changeParamRestoreInfo,
		changeNomineesInfo,
		changeTermsInfo,
		getSimpleReapplicationsConditionsDetailDataInfo,
		getAddressInfo,
		reapplyHomeservice,
		reapplyLoan,
		changeAddressInfo,
		changeBusinessAddressInfo,
		handleReApplyHomeservice,
		setHsReapplyInfo,
		isReapplyCallbackEnter,
		callbackEnterInfo,
		goBackWithQueryString,
		resetReapplyInfo,
		changeShippingAddressInfo,
	};
}
